/**
 * Pure helpers for the Connect external source wizard.
 *
 * Extracted so the validation contract can be unit-tested without
 * pulling in the React render tree.
 */

/**
 * #699 — return the trimmed user-entered display name, or `null` if
 * the user submitted only whitespace.
 *
 * Background: the wizard's `canProceed()` already gates the user out
 * of the submit step until `displayName.trim().length > 0`, so the
 * previous `displayName || PROVIDER_META[kind].label` fallback in the
 * submit handler was both (a) redundant and (b) a hidden contract: a
 * user who managed to bypass the gate (browser autofill, devtools)
 * would get a provider-label connector silently created on their
 * behalf, contradicting the wizard's "required" affordance.
 *
 * Returning `null` (vs. an empty string) lets callers fail loudly
 * rather than silently substitute.
 */
export function resolveConnectorDisplayName(
  rawInput: string | undefined | null,
): string | null {
  if (rawInput == null) return null;
  const trimmed = rawInput.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Mirror of `canProceed()` for the wizard's Configure step, exposed for
 * tests. Returns true exactly when the submit handler is allowed to
 * call `create.mutateAsync({ displayName: <user-trimmed value> })`
 * without a fallback substitution.
 */
export function canSubmitConfigStep(
  displayName: string | undefined | null,
  kind: string | null,
): boolean {
  const resolved = resolveConnectorDisplayName(displayName ?? null);
  return resolved !== null && kind !== null;
}
